import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { BookmarkPlus, BookmarkCheck, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  followTender, followRegion, followCpv, followEntity, followKeyword,
  listSubscriptions, deleteSubscription,
} from '../../lib/api';

/**
 * FollowButton — przycisk "Follow" dla różnych typów obiektów.
 *
 * Props:
 *   type: 'tender' | 'region' | 'cpv' | 'entity' | 'keyword'
 *   id / name / params (zależne od type)
 *   variant / size — passthrough do Button
 *
 * Przykłady:
 *   <FollowButton type="tender" id={tenderSourceId} />
 *   <FollowButton type="region" region="mazowieckie" />
 *   <FollowButton type="cpv" cpv="45233100" />
 *   <FollowButton type="entity" nip="123" name="Firma X" />
 *   <FollowButton type="keyword" keyword="oświetlenie" />
 */
export function FollowButton({
  type,
  id,
  region,
  cpv,
  nip,
  name,
  keyword,
  variant = 'outline',
  size = 'sm',
  label,
  labelActive,
  className = '',
}) {
  const [isFollowed, setIsFollowed] = useState(false);
  const [subId, setSubId] = useState(null);
  const [loading, setLoading] = useState(false);

  // Check current state on mount
  useEffect(() => {
    let cancelled = false;
    listSubscriptions().then(resp => {
      if (cancelled) return;
      const items = resp.items || [];
      let match = null;
      if (type === 'tender' && id) {
        match = items.find(s => s.filter_spec?.tender_ids?.includes(id));
      } else if (type === 'region' && region) {
        match = items.find(s => s.filter_spec?.regions?.includes(region));
      } else if (type === 'cpv' && cpv) {
        match = items.find(s =>
          s.filter_spec?.cpv_codes?.includes(cpv) ||
          s.filter_spec?.cpv_prefixes?.includes(cpv)
        );
      } else if (type === 'entity' && nip) {
        match = items.find(s => s.filter_spec?.entity_nips?.includes(nip));
      } else if (type === 'keyword' && keyword) {
        match = items.find(s => s.filter_spec?.keywords?.includes(keyword));
      }
      if (match) {
        setIsFollowed(true);
        setSubId(match.id);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [type, id, region, cpv, nip, keyword]);

  const handleToggle = async () => {
    setLoading(true);
    try {
      if (isFollowed && subId) {
        await deleteSubscription(subId);
        setIsFollowed(false);
        setSubId(null);
        toast.success('Usunięto z obserwowanych');
      } else {
        let r;
        if (type === 'tender') r = await followTender(id);
        else if (type === 'region') r = await followRegion(region);
        else if (type === 'cpv') r = await followCpv(cpv);
        else if (type === 'entity') r = await followEntity(nip, name);
        else if (type === 'keyword') r = await followKeyword(keyword);

        if (r?.already_subscribed) {
          setIsFollowed(true);
          setSubId(r.subscription_id);
          toast.info('Już subskrybujesz');
        } else if (r?.created) {
          setIsFollowed(true);
          setSubId(r.subscription_id);
          toast.success('Dodano do obserwowanych');
        }
      }
    } catch (e) {
      toast.error('Błąd: ' + (e.response?.data?.detail || e.message));
    }
    setLoading(false);
  };

  const buttonLabel = isFollowed
    ? (labelActive || 'Obserwowane')
    : (label || 'Follow');

  return (
    <Button
      variant={isFollowed ? 'outline' : variant}
      size={size}
      onClick={handleToggle}
      disabled={loading}
      className={className}
    >
      {loading ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : isFollowed ? (
        <BookmarkCheck className="w-4 h-4 text-primary" />
      ) : (
        <BookmarkPlus className="w-4 h-4" />
      )}
      <span className="ml-1.5">{buttonLabel}</span>
    </Button>
  );
}

export default FollowButton;
